'use client';

import React, { useEffect, useRef, useState } from 'react';
import type { SearchModalProps } from './types';
import { ModalLayerContext } from './modalLayer';

export default function SearchModal({ isOpen, onClose, children }: SearchModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  // The same element as state, so overlays portalling into it (the offer
  // popup — see modalLayer.ts) re-render once it exists.
  const [layer, setLayer] = useState<HTMLDialogElement | null>(null);
  const setDialog = React.useCallback((el: HTMLDialogElement | null) => {
    dialogRef.current = el;
    setLayer(el);
  }, []);
  // True when the dialog is open but NOT in the browser's top layer — see the
  // degraded-open warning below. Arms the click-away fallback.
  const [degraded, setDegraded] = useState(false);

  // Promote the modal into the browser's TOP LAYER via the native <dialog>
  // showModal(). The top layer renders above ALL host page content regardless
  // of z-index — essential for an embedded widget: some host sites use absurd
  // z-index values (e.g. syfakb2b.gr's fixed header at z-index 99999999999)
  // that would otherwise paint over the modal. Driven by `isOpen`.
  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    if (isOpen) {
      if (!d.open) {
        if (typeof d.showModal === 'function') {
          try {
            d.showModal();
          } catch {
            // showModal() throws InvalidStateError when the dialog already
            // carries the `open` attribute. RECOVER rather than degrade: close
            // it and ask again. `d.open = true` is a last resort, not a fix —
            // see the warning below for why it is so damaging.
            try {
              d.close();
              d.showModal();
            } catch {
              d.open = true;
            }
          }
        } else {
          // Legacy browsers without showModal: visible but not top-layer.
          d.open = true;
        }
      }

      // ⚠️ DEGRADED OPEN — the failure mode that reads as "the page is frozen".
      // `:modal` is true ONLY after a successful showModal(). If we got here by
      // the `d.open = true` fallback the dialog is visible but NOT in the top
      // layer, and three things silently break at once:
      //   1. the host's own z-index wins, so their header paints over us;
      //   2. a click on the backdrop lands on THEIR page, never reaches our
      //      onClick, and so cannot close the modal — "clicking outside does
      //      nothing";
      //   3. the body scroll-lock below still applies, so the page cannot
      //      scroll either.
      // Which is indistinguishable, to a user, from the page having frozen.
      // Until now this happened in complete silence. Say so loudly: `isModal`
      // in the console is the single reading that separates this from every
      // other cause, and nobody can read it from outside the browser.
      if (typeof d.matches === 'function' && d.open && !d.matches(':modal')) {
        setDegraded(true);
        console.warn(
          '[search] The modal opened WITHOUT the browser top layer. The host page ' +
            'can paint over it and clicks outside it cannot reach it. A backdrop ' +
            'click-away fallback has been enabled. Please report this: ' +
            `isModal=false, dialogOpen=${d.open}, ua=${navigator.userAgent}`,
        );
      } else {
        setDegraded(false);
      }
      // Put the caret in the search box. This has to happen HERE, after
      // showModal(), and cannot be left to React's `autoFocus` on the input:
      // React implements that prop by calling .focus() during commit, and it
      // does NOT render a real `autofocus` ATTRIBUTE. showModal() then runs the
      // dialog's own focusing steps, which look for exactly that attribute,
      // find none, and focus the first focusable child instead — measured on
      // syfak's live embed (2026-09-10) that is a <button type="submit">, so the
      // shopper had to click into the box before typing. Requested by their team.
      //
      // md+ only, deliberately: on a phone this pops the keyboard the instant
      // the modal opens, and the iOS 100vh-vs-keyboard question is still open
      // (§5.18). Chat autofocus was gated the same way for the same reason.
      // Tapping the box on a phone still focuses it as it always did.
      if (typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches) {
        d.querySelector<HTMLInputElement>('input[type="text"]')?.focus();
      } else {
        // Phones: on iPhone the search box refuses this programmatic focus, so
        // showModal()'s focusing steps settle on the first button — the ✕ —
        // and iOS draws its blue focus box around it the moment the search
        // opens (founder's iPhone, 2026-11-03). Only in that case, hold focus
        // on the dialog itself (tabIndex -1, no outline): still inside the
        // modal for screen readers and Tab, no ring, no keyboard. Where the
        // box did take focus (Chrome on Android), nothing changes.
        const active = (d.getRootNode() as Document | ShadowRoot).activeElement;
        if (active instanceof HTMLButtonElement) d.focus({ preventScroll: true });
      }
    } else if (d.open) {
      d.close();
    }
  }, [isOpen]);

  // ── Click-away fallback, DEGRADED OPEN ONLY ──────────────────────
  // A properly modal dialog cannot be clicked past: the browser blocks the
  // page behind it, so the host document never sees these clicks and this
  // listener is inert. It engages ONLY when the top layer was lost, which is
  // exactly when our own backdrop handler stops being reachable. So this adds
  // no behaviour to the normal path — it restores the missing one.
  useEffect(() => {
    if (!isOpen || !degraded) return;
    const d = dialogRef.current;
    if (!d) return;

    // Ignore the click that OPENED us: on a host trigger the same click would
    // otherwise bubble to the document and close the modal immediately.
    let armed = false;
    const arm = setTimeout(() => { armed = true; }, 0);

    const onDocClick = (e: MouseEvent) => {
      if (!armed) return;
      // composedPath() pierces the Shadow DOM, so any click inside the widget
      // contains the dialog itself while a click on the host page does not.
      // A click on our own backdrop also contains it — handled by the dialog's
      // onClick below, so this never double-fires.
      const path = typeof e.composedPath === 'function' ? e.composedPath() : [];
      if (path.includes(d)) return;
      onClose();
    };

    document.addEventListener('click', onDocClick, true);
    return () => {
      clearTimeout(arm);
      document.removeEventListener('click', onDocClick, true);
    };
  }, [isOpen, degraded, onClose]);

  // Host page scroll lock while open — on BOTH <html> and <body>, as
  // `!important` inline styles, restoring exactly what was there on close.
  //
  // 🔴 A plain `body.style.overflow = 'hidden'` is NOT enough on a real host.
  // syfak's theme (digital4u custom_stylesheet.css) ships
  // `body { overflow: auto !important; }`, and a stylesheet !important beats a
  // normal inline style — so for months the lock silently did nothing there and
  // the wheel over the backdrop scrolled their page (reported 2026-10-26).
  // Measured on syfakb2b.gr: computed overflow stayed `auto` with the plain
  // set, became `hidden` with the !important one. <html> is locked too because
  // a host that gives <html> its own overflow (syfak's cart page:
  // `html.checkout-cart-cart { overflow-x: hidden }`) stops body's value from
  // reaching the viewport, and then a body-only lock no longer holds.
  //
  // Phones also HIDE the host page while the search is open (it is fully
  // covered anyway). When iOS slides the page for a focused field, the area
  // behind its see-through keyboard is drawn from the host document, outside
  // anything we can paint over — kostaboda.gr's banners showed there
  // (founder's iPhone, 2026-11-03). With the host invisible and <html> white,
  // there is nothing left to show. The dialog sets `visibility: visible`
  // itself, so it is unaffected.
  useEffect(() => {
    if (!isOpen) return;
    const html = document.documentElement;
    const body = document.body;
    const set: Array<[HTMLElement, string, string]> = [
      [html, 'overflow', 'hidden'],
      [body, 'overflow', 'hidden'],
    ];
    if (window.matchMedia('(max-width: 639.98px)').matches) {
      set.push([body, 'visibility', 'hidden'], [html, 'background-color', '#ffffff']);
    }
    const saved = set.map(([el, prop]) => ({
      el,
      prop,
      value: el.style.getPropertyValue(prop),
      priority: el.style.getPropertyPriority(prop),
    }));
    for (const [el, prop, value] of set) el.style.setProperty(prop, value, 'important');
    return () => {
      for (const { el, prop, value, priority } of saved) {
        if (value) el.style.setProperty(prop, value, priority);
        else el.style.removeProperty(prop);
      }
    };
  }, [isOpen]);

  // Fit the dialog to what the shopper can actually SEE.
  //
  // `h-screen` (100vh) is only the fallback. On iPhone 100vh is the height with
  // the browser bars HIDDEN, so the bottom of the modal sat under Safari's /
  // Chrome's bottom toolbar — and the bottom-most control, Αριάδνη's input,
  // simply was not there (syfak, 2026-10-27). And when the keyboard opens, iOS
  // does not shrink the page at all: it slides the visible area down over it,
  // so a full-height fixed modal is partly behind the keyboard and partly
  // scrolled off the top.
  //
  // `visualViewport` is exactly the visible rectangle: `height` excludes the
  // browser bars and the keyboard, `offsetTop` is how far iOS slid it. Pinning
  // the dialog to that keeps the whole modal — header to input — on screen in
  // every one of those states. On desktop it equals the window, so nothing
  // changes there.
  //
  // Skipped while the page is pinch-ZOOMED (scale ≠ 1): the visible rectangle
  // is then a magnified part of the page, and shrinking the modal to it would
  // fight the shopper's own gesture. 100vh takes over until they zoom back.
  useEffect(() => {
    if (!isOpen) return;
    const d = dialogRef.current;
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!d || !vv) return;
    // The visible height with no keyboard up, remembered for the moment the
    // keyboard goes away (below): measured while nobody types, otherwise the
    // tallest seen (Android can open with the box already focused), and
    // forgotten when the width changes (rotation).
    let restHeight = 0;
    let restWidth = 0;
    const typing = () => {
      const a = (d.getRootNode() as Document | ShadowRoot).activeElement;
      return a instanceof HTMLInputElement || a instanceof HTMLTextAreaElement;
    };
    // Keyboard handling is for on-screen keyboards only: on a desktop a
    // window resized while typing must simply be followed.
    //
    // ⚠️ Never change the layout in a focus handler. A tap focuses the field
    // BEFORE its click is delivered, so moving things on focus moves them out
    // from under the finger: an attempt to shrink the chat above the keyboard
    // on focusin (60eb0b1) made the click land on the backdrop and CLOSE the
    // search (founder's iPhone, 2026-11-03). Layout changes wait for the next
    // frame, after the click (see onFocusOut).
    const touch = window.matchMedia('(pointer: coarse)').matches;
    // When the keyboard opens for a box low on the screen (the chat), iOS
    // slides the page up — but it reports where the slide will END at its
    // START, and only begins sliding ~30ms later, over ~0.2s. Jumping straight
    // there put the chat too low with white above it until iOS caught up: a
    // drop, a flash, and a glide back (founder's iPhone recording, stepped at
    // 30fps, 2026-11-03). So that one move GLIDES on the same schedule as
    // iOS's slide — the two cancel out on screen. Top and height move
    // together, so the bottom edge (the chat box) stays with the keyboard.
    const GLIDE = 'top 200ms cubic-bezier(0.42, 0, 0.58, 1) 30ms, height 200ms cubic-bezier(0.42, 0, 0.58, 1) 30ms';
    let lastTop = 0;
    let glideEnd = 0;
    // The keyboard's height (how much visible area it takes), learned from
    // an opening the browser did not slide, remembered per screen width on
    // this device. Used to lift a bottom box BEFORE the keyboard opens (see
    // onTouchEnd). Until learned: half the screen, which errs high — a box a
    // little too high settles down; one too low makes the browser slide.
    const KB_KEY = 'flexifind:kb:';
    let kbHeight = 0;
    const loadKb = () => {
      try {
        kbHeight = Number(localStorage.getItem(KB_KEY + restWidth)) || 0;
      } catch {
        kbHeight = 0;
      }
    };
    const stopGlide = () => {
      window.clearTimeout(glideEnd);
      d.style.removeProperty('transition');
    };
    const fit = () => {
      if (Math.abs(vv.scale - 1) > 0.01) {
        stopGlide();
        d.style.removeProperty('height');
        d.style.removeProperty('top');
        return;
      }
      if (vv.width !== restWidth) {
        restWidth = vv.width;
        restHeight = 0;
        loadKb();
      }
      restHeight = typing() ? Math.max(restHeight, vv.height) : vv.height;
      if (touch && typing() && vv.offsetTop < 1 && vv.height < restHeight - 150) {
        const kb = Math.round(restHeight - vv.height);
        if (kb !== kbHeight) {
          kbHeight = kb;
          try {
            localStorage.setItem(KB_KEY + restWidth, String(kb));
          } catch {
            /* private mode: remembered for this page only */
          }
        }
      }
      // iOS sends `resize` and `scroll` together: the second call must not
      // cancel the glide the first one started — it runs out, then clears.
      if (touch && typing() && vv.offsetTop - lastTop > 50) {
        d.style.setProperty('transition', GLIDE);
        window.clearTimeout(glideEnd);
        glideEnd = window.setTimeout(stopGlide, 300);
      }
      d.style.setProperty('height', `${vv.height}px`);
      d.style.setProperty('top', `${vv.offsetTop}px`);
      lastTop = vv.offsetTop;
    };
    // The keyboard is going away (✓, or a tap outside the box). iOS reports
    // the new visible size only when the keyboard has finished sliding down,
    // so for about half a second the space it frees was blank white under the
    // still-short modal (founder's iPhone, 2026-11-03). Grow back to the
    // keyboard-less height at once — the products are then already there as
    // the keyboard slides off; the resize at the end only confirms it.
    // Only when the visible area is clearly keyboard-short (a hardware
    // keyboard shrinks nothing), and not when focus just moves to another box.
    let settle = 0;
    const onFocusOut = () => {
      requestAnimationFrame(() => {
        if (typing() || !restHeight || Math.abs(vv.scale - 1) > 0.01) return;
        if (!touch) return;
        if (vv.height > restHeight - 150) return;
        stopGlide();
        d.style.setProperty('height', `${restHeight}px`);
        d.style.setProperty('top', '0px');
        lastTop = 0;
        // Safety net: if iOS never sends the resize, settle on the truth.
        window.clearTimeout(settle);
        settle = window.setTimeout(fit, 1000);
      });
    };
    // A box low on the screen (the chat) sits where the keyboard will
    // appear, so the browser slides the whole page up to show it — and every
    // browser does that on its own schedule (Safari ~30ms late over ~0.2s,
    // Chrome on iPhone differently), so following the slide always showed a
    // drop and a jump back (founder's Safari recording and Chrome screenshots,
    // 2026-11-03). Instead the slide is made unnecessary: on a TAP of such a
    // box we take the tap over — cancel the browser's own handling (so no
    // click can land anywhere else: 60eb0b1 closed the search that way), lift
    // the modal above where the keyboard will be, then focus the box
    // ourselves. Focusing inside the tap still opens the keyboard, and the box
    // is already in view, so nothing slides.
    let touchFrom: { x: number; y: number } | null = null;
    let fallback = 0;
    const field = (t: EventTarget | null) =>
      t instanceof HTMLTextAreaElement || (t instanceof HTMLInputElement && t.type !== 'checkbox' && t.type !== 'radio')
        ? (t as HTMLTextAreaElement | HTMLInputElement)
        : null;
    const onTouchStart = (e: TouchEvent) => {
      const p = e.touches[0];
      touchFrom = e.touches.length === 1 && p ? { x: p.clientX, y: p.clientY } : null;
    };
    const onTouchMove = (e: TouchEvent) => {
      const p = e.touches[0];
      if (touchFrom && p && Math.hypot(p.clientX - touchFrom.x, p.clientY - touchFrom.y) > 10) touchFrom = null;
    };
    const onTouchEnd = (e: TouchEvent) => {
      const from = touchFrom;
      touchFrom = null;
      if (!from || !touch || !restHeight) return;
      // Keyboard up: a tap on a product (a card, or any link — the chat's
      // product links) only puts the keyboard away. Tapping above the keyboard
      // to dismiss it used to open the product beneath and leave the search
      // (founder, iPhone, 2026-11-03). Other controls (✕, send, chips, the
      // «Φίλτρα» button) still work as usual.
      if (typing() && vv.height < restHeight - 150 && !field(e.target)) {
        const el = e.target instanceof Element ? e.target : null;
        if (el?.closest('[data-product-id], a[href]')) {
          e.preventDefault(); // no click: the product is not opened
          ((d.getRootNode() as Document | ShadowRoot).activeElement as HTMLElement | null)?.blur();
          return;
        }
      }
      const box = field(e.target);
      if (!box || box.disabled || box.readOnly) return;
      if (Math.abs(vv.scale - 1) > 0.01 || vv.height < restHeight - 150) return; // zoomed, or keyboard already up
      if ((d.getRootNode() as Document | ShadowRoot).activeElement === box) return; // moving the caret
      if (box.getBoundingClientRect().top <= restHeight / 2) return; // top boxes need no help
      e.preventDefault();
      const kb = kbHeight || Math.round(restHeight * 0.5);
      stopGlide();
      d.style.setProperty('height', `${restHeight - kb}px`);
      d.style.setProperty('top', '0px');
      lastTop = 0;
      box.focus({ preventScroll: true });
      // No on-screen keyboard came (a hardware one): back to the full height.
      window.clearTimeout(fallback);
      fallback = window.setTimeout(() => {
        if (vv.height > restHeight - 150) fit();
      }, 900);
    };
    fit();
    vv.addEventListener('resize', fit);
    vv.addEventListener('scroll', fit);
    d.addEventListener('focusout', onFocusOut);
    d.addEventListener('touchstart', onTouchStart, { passive: true });
    d.addEventListener('touchmove', onTouchMove, { passive: true });
    d.addEventListener('touchend', onTouchEnd, { passive: false });
    return () => {
      vv.removeEventListener('resize', fit);
      vv.removeEventListener('scroll', fit);
      d.removeEventListener('focusout', onFocusOut);
      d.removeEventListener('touchstart', onTouchStart);
      d.removeEventListener('touchmove', onTouchMove);
      d.removeEventListener('touchend', onTouchEnd);
      window.clearTimeout(fallback);
      window.clearTimeout(settle);
      stopGlide();
      d.style.removeProperty('height');
      d.style.removeProperty('top');
    };
  }, [isOpen]);

  // No swipe-to-close. The phone version listened on the whole window and
  // closed on any >100px downward drag — i.e. the ordinary gesture for
  // scrolling results or the chat history back UP — and, because the end
  // position was never reset, even a plain tap above where the previous swipe
  // ended. Removed 2026-10-26; the X and «Επιστροφή στην αναζήτηση» close it.

  return (
    <dialog
      ref={setDialog}
      aria-label="Search"
      // Native ESC fires `cancel`; drive the close through React state so the
      // dialog element and `isOpen` stay in sync (the effect calls .close()).
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      // A click that lands on the dialog itself = the gutter/backdrop area
      // around the box (the box below stops propagation), so close.
      //
      // 🔴 And STOP it here too, exactly as the box does for the X. A click
      // does not end at our shadow root — it carries on up into the HOST page,
      // and a host may wrap us inside its own trigger. syfak's live header is
      // `<li onclick="openSearch()">…#my-search-wrapper…</li>`, so an
      // unstopped backdrop click ran our close (→ their closeSearch(), panel
      // hidden) and then, the same millisecond, THEIR openSearch() (panel shown
      // again). Our modal closed, their panel stayed "open", so the next click
      // on the search icon changed nothing and the search never reopened —
      // the "frozen" search syfak kept reporting (measured on their live,
      // logged-in page 2026-09-16). Closing via the X never leaked, which is
      // why that path always worked. Both close paths now look identical to
      // the host.
      onClick={(e) => {
        if (e.target === dialogRef.current) {
          e.stopPropagation();
          onClose();
        }
      }}
      tabIndex={-1}
      className="search-modal-dialog fixed inset-0 m-0 w-screen h-screen max-w-none max-h-none p-0 sm:p-4 border-0 bg-transparent flex items-center justify-center overscroll-contain outline-none visible"
    >
      {isOpen && (
        <div
          // max-h-full: on a tablet the 90vh box must still fit when the
          // keyboard has shrunk the dialog (see the visualViewport effect).
          className="bg-white w-full h-full sm:h-[90vh] lg:h-[85vh] max-h-full sm:max-w-[95vw] lg:max-w-[1400px] flex flex-col overflow-y-auto overscroll-contain custom-scrollbar animate-slideUp rounded-none shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <ModalLayerContext.Provider value={layer}>{children}</ModalLayerContext.Provider>
        </div>
      )}
    </dialog>
  );
}
